import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase, isSupabaseConfigured } from '../utils/supabaseClient';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isSupabaseConfigured || !supabase) {
      // Check for local fallback session if Supabase is offline or not configured
      const localSession = localStorage.getItem('certiflow_admin_session');
      if (localSession) {
        try {
          setSession(JSON.parse(localSession));
        } catch {
          // ignore parsing error
        }
      }
      setLoading(false);
      return;
    }

    // Get current session from Supabase
    supabase.auth.getSession()
      .then(({ data: { session } }) => {
        setSession(session);
        setLoading(false);
      })
      .catch((err) => {
        console.warn('[AuthContext] getSession error:', err);
        setLoading(false);
      });

    // Listen for auth changes
    const { data: { subscription } = {} } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    }) || {};

    return () => {
      if (subscription?.unsubscribe) {
        subscription.unsubscribe();
      }
    };
  }, []);

  const login = async (email, password) => {
    // If Supabase is not configured, support local administrator fallback authentication
    if (!isSupabaseConfigured || !supabase) {
      if (email && password) {
        const fallbackSession = {
          user: {
            id: 'admin-local',
            email: email.trim(),
            role: 'authenticated',
            user_metadata: { name: 'Admin' },
          },
          access_token: 'local-demo-token',
        };
        localStorage.setItem('certiflow_admin_session', JSON.stringify(fallbackSession));
        setSession(fallbackSession);
        return { session: fallbackSession, user: fallbackSession.user };
      }
      throw new Error('Please enter both email and password.');
    }

    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const logout = async () => {
    if (supabase) {
      try {
        await supabase.auth.signOut();
      } catch (e) {
        console.warn('[AuthContext] signOut error:', e);
      }
    }
    localStorage.removeItem('certiflow_admin_session');
    setSession(null);
  };

  return (
    <AuthContext.Provider value={{ session, loading, login, logout, isLoggedIn: !!session }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
